import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { resetPassword } from '../../../app/store/slices/authSlice';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import logo from '../../../assets/images/logos/576_Cash Canvas-02.png';

const ResetPassword = () => {
  const [formData, setFormData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false
  });
  const [passwordStrength, setPasswordStrength] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { status } = useSelector((state) => state.auth);

  useState(() => {
    if (location.state?.email) {
      setFormData(prev => ({ ...prev, email: location.state.email }));
    }
  }, [location.state]);

  const checkPasswordStrength = (password) => {
    const strongRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    const mediumRegex = /^(?=.*[a-zA-Z])(?=.*\d).{6,}$/;
  
    if (strongRegex.test(password)) return 'Strong';
    if (mediumRegex.test(password)) return 'Medium';
    return 'Weak';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'newPassword') {
      const strength = checkPasswordStrength(value);
      setPasswordStrength(strength);
    }
  };

  const handleTogglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validations
    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      const result = await dispatch(resetPassword({
        email: formData.email,
        otp: formData.otp,
        newPassword: formData.newPassword
      })).unwrap();

      // Handle API Response
      if (result.error) {
        switch (result.error.code) {
          case 'OTP_EXPIRED':
            toast.error('Verification code has expired. Please request a new one.');
            navigate('/forgot-password', { 
              state: { email: formData.email } 
            });
            break;

          case 'INVALID_OTP':
            toast.error('Incorrect verification code');
            setFormData(prev => ({ ...prev, otp: '' }));
            break;

          case 'SAME_PASSWORD':
            toast.error('New password cannot be the same as your current password');
            setFormData(prev => ({
              ...prev,
              newPassword: '',
              confirmPassword: ''
            }));
            break;

          case 'RESET_ERROR':
            toast.error('Failed to reset password. Please try again.');
            break;

          default:
            toast.error(result.message || 'Password reset failed');
        }
        return;
      }

      // Success case
      if (result.status === 200) {
        toast.success('Password has been reset successfully!');
        navigate('/login', {
          state: { message: 'Password reset successful. Please login with your new password.' }
        });
      }
    } catch (err) {
      // Handle network/connection errors
      if (err.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection.');
      } else if (err.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
      } else {
        toast.error('An unexpected error occurred. Please try again.');
        console.error('Reset password error:', err);
      }
    }
  };

  return (
    <div className="flex-1 min-h-screen flex items-center justify-center bg-gray-50 px-4 font-sans">
      <div className="fixed top-0 left-0 p-4">
        <img src={logo} alt="Cash Canvas Logo"
         className="h-24 w-auto object-contain max-w-[150px]" />
      </div>
    
      <div className="auth-ui-card">
        <h2 className="txt-page-heading">Reset Password</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <input
            type="email"
            name="email"
            placeholder="Email"
            className="input-field"
            autoComplete="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
          
          <input
            type="text"
            name="otp"
            placeholder="Enter OTP"
            className="input-field"
            value={formData.otp}
            onChange={handleChange}
            required
            maxLength={6}
          />

          <div className="relative">
            <input
              type={showPassword.newPassword ? 'text' : 'password'}
              name="newPassword"
              placeholder="New Password"
              className="input-field"
              value={formData.newPassword}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              onClick={() => handleTogglePassword('newPassword')}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showPassword.newPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <div className="relative">
            <input
              type={showPassword.confirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              placeholder="Confirm New Password"
              className="input-field"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              onClick={() => handleTogglePassword('confirmPassword')}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500"
            >
              {showPassword.confirmPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {formData.newPassword && (
            <div className="text-sm mt-2 mb-4">
              {passwordStrength !== 'Strong' && (
                <div className="space-y-1.5">
                  <p className="text-gray-600">Password must contain:</p>
                  <div className="flex flex-wrap items-center gap-x-2 text-sm">
                    <span className={!(/[A-Z]/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/[A-Z]/.test(formData.newPassword)) ? '✗' : '✓'} One Uppercase Letter
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className={!(/\d/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/\d/.test(formData.newPassword)) ? '✗' : '✓'} One Number
                    </span>
                    <span className="text-gray-400"></span>
                    <span className={!(/[\W_]/.test(formData.newPassword)) ? 'text-red-500' : 'text-green-500'}>
                      {!(/[\W_]/.test(formData.newPassword)) ? '✗' : '✓'} One Special Character
                    </span>
                    <span className="text-gray-400">•</span>
                    <span className={formData.newPassword.length < 8 ? 'text-red-500' : 'text-green-500'}>
                      {formData.newPassword.length < 8 ? '✗' : '✓'} Minimum 8+ Characters
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            className="submit-btn"
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-btn-primary hover:text-btn-primary-hover font-medium"
          >
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;